import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import QuinceApiError from '@src/quince-site-common/src/domains/quince-api-error'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import { eraseFromLocalStorage } from '@src/quince-site-common/src/utils/eraseFromLocalStorageWithExpiry'
import { getFromLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/getFromLocalStorageWithExpiry'
import { getPageContextDetails } from '@src/quince-site-common/src/utils/getPageContextDetails'
import { getIsHomePage } from '@src/quince-site-common/src/utils/getPageType'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { setInLocalStorageWithExpiry } from '@src/quince-site-common/src/utils/setInLocalStorageWithExpiry'

export const FINDING_METHOD_AGGREGATE = {
  SEARCH: 'Search',
  RECOMMENDATION: 'Recommendation',
  NAVIGATION: 'Navigation',
  NAVIGATION_MOBILE_MENU: 'Navigation Mobile Menu',
  CAMPAIGN: 'Campaign',
  WISHLIST: 'Wishlist',
  MY_ORDERS: 'My Orders',
  LANDING_PAGE: 'Landing Page',
}

export const FINDING_METHOD_GRANULAR = {
  WISHLIST: 'Wishlist',
  MY_ORDERS: 'My Orders',
}

export const FINDING_METHOD_ID = {
  SEARCH: 'search',
  WISHLIST: 'wishlist',
  ORDERS: 'orders',
  TOP_NAV: 'top-nav',
  TOP_MENU: 'top-menu',
}

export interface FindingMethodDetails {
  findingMethodAggregate: FindingMethodType
  findingMethodGranular: string
  findingMethodId: string
}

const captureFindingMethodException = (error: unknown) => {
  const quinceError = new QuinceApiError({
    error: error as Error,
    message: 'Error in finding method',
    apiName: 'FindingMethodTracker',
  })
  captureException(quinceError, {
    module: MODULES.INTERNAL_ANALYTICS,
    feature: FEATURES.FINDING_METHODS,
  })
}

export type FindingMethodType = (typeof FINDING_METHOD_AGGREGATE)[keyof typeof FINDING_METHOD_AGGREGATE]

export const getFindingMethods = (): Record<string, FindingMethodDetails> => {
  try {
    const findingMethodsLB = getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.FINDING_METHODS_LB)
    if (findingMethodsLB) {
      return JSON.parse(findingMethodsLB)
    }

    const findingMethods = getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.FINDING_METHODS, true, false)
    if (findingMethods) {
      const parsedMethods = JSON.parse(findingMethods) as Record<string, FindingMethodDetails>

      setInLocalStorageWithExpiry(ANALYTIC_COOKIES.FINDING_METHODS_LB, JSON.stringify(parsedMethods))
      eraseFromLocalStorage(ANALYTIC_COOKIES.FINDING_METHODS)

      return parsedMethods
    } else {
      eraseFromLocalStorage(ANALYTIC_COOKIES.FINDING_METHODS)
    }

    return {}
  } catch (e) {
    captureFindingMethodException(e)
    eraseFromLocalStorage(ANALYTIC_COOKIES.FINDING_METHODS)
    return {}
  }
}

export const setFindingMethodForVariant = (variantId: string) => {
  const currentFindingMethod = getCurrentFindingMethod()
  const findingMethods = getFindingMethods()
  if (currentFindingMethod) {
    findingMethods[variantId] = currentFindingMethod
  }
  setInLocalStorageWithExpiry(ANALYTIC_COOKIES.FINDING_METHODS_LB, JSON.stringify(findingMethods))
  eraseFromLocalStorage(ANALYTIC_COOKIES.FINDING_METHODS)
}

export const clearFindingMethodForVariant = (variantId: string) => {
  const currentFindingMethod = getCurrentFindingMethod()
  const findingMethods: Record<string, FindingMethodDetails | undefined> = getFindingMethods()
  if (currentFindingMethod) {
    findingMethods[variantId] = undefined
  }
  setInLocalStorageWithExpiry(ANALYTIC_COOKIES.FINDING_METHODS_LB, JSON.stringify(findingMethods))
  eraseFromLocalStorage(ANALYTIC_COOKIES.FINDING_METHODS)
}

export const getCurrentFindingMethod = (): FindingMethodDetails | undefined => {
  try {
    const currentFindingMethodLB = getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.CURRENT_FINDING_METHOD_LB)
    if (currentFindingMethodLB) {
      return JSON.parse(currentFindingMethodLB)
    }

    const currentFindingMethod = getFromLocalStorageWithExpiry(ANALYTIC_COOKIES.CURRENT_FINDING_METHOD, true, false)
    if (currentFindingMethod) {
      const parsedMethod = JSON.parse(currentFindingMethod) as FindingMethodDetails

      setInLocalStorageWithExpiry(ANALYTIC_COOKIES.CURRENT_FINDING_METHOD_LB, JSON.stringify(parsedMethod))
      eraseFromLocalStorage(ANALYTIC_COOKIES.CURRENT_FINDING_METHOD)

      return parsedMethod
    } else {
      eraseFromLocalStorage(ANALYTIC_COOKIES.CURRENT_FINDING_METHOD)
    }

    return undefined
  } catch (e) {
    captureFindingMethodException(e)
    eraseFromLocalStorage(ANALYTIC_COOKIES.CURRENT_FINDING_METHOD)
    return undefined
  }
}

export const setCurrentFindingMethod = ({
  findingMethodId,
  findingMethodGranular,
  findingMethodAggregate,
}: FindingMethodDetails) => {
  setInLocalStorageWithExpiry(
    ANALYTIC_COOKIES.CURRENT_FINDING_METHOD_LB,
    JSON.stringify({ findingMethodId, findingMethodGranular, findingMethodAggregate }),
  )
  eraseFromLocalStorage(ANALYTIC_COOKIES.CURRENT_FINDING_METHOD)
}

export const setFindingMethod = {
  [FINDING_METHOD_AGGREGATE.SEARCH]: () => {
    const { currentPage } = getPageContextDetails()
    setCurrentFindingMethod({
      findingMethodAggregate: FINDING_METHOD_AGGREGATE.SEARCH,
      findingMethodGranular: `Search | ${currentPage.page_type}`,
      findingMethodId: FINDING_METHOD_ID.SEARCH,
    })
  },
  [FINDING_METHOD_AGGREGATE.RECOMMENDATION]: (elementInfo?: { elementName?: string; elementId?: string }) => {
    const { currentPage } = getPageContextDetails()
    const { elementName, elementId } = elementInfo ?? {}
    setCurrentFindingMethod({
      findingMethodAggregate: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
      findingMethodGranular: `Recommendation | ${elementName} | ${currentPage.page_type}`,
      findingMethodId: elementId ?? '',
    })
  },
  [FINDING_METHOD_AGGREGATE.NAVIGATION]: (navData?: { L1?: string; L2?: string; L3?: string }) => {
    const { L1, L2, L3 } = navData ?? {}
    const { currentPage } = getPageContextDetails()
    setCurrentFindingMethod({
      findingMethodAggregate: FINDING_METHOD_AGGREGATE.NAVIGATION,
      findingMethodGranular: `Navigation | Top Nav | ${L1 ? L1 + ' | ' : ''} ${
        L2 ? L2 + ' | ' : ''
      } ${L3 ? L3 + ' | ' : ''} ${currentPage.page_type}`,
      findingMethodId: FINDING_METHOD_ID.TOP_NAV,
    })
  },
  [FINDING_METHOD_AGGREGATE.NAVIGATION_MOBILE_MENU]: (navData?: { L1?: string; L2?: string; L3?: string }) => {
    const { L1, L2, L3 } = navData ?? {}
    const { currentPage } = getPageContextDetails()
    setCurrentFindingMethod({
      findingMethodAggregate: FINDING_METHOD_AGGREGATE.NAVIGATION,
      findingMethodGranular: `Navigation | Top Menu | ${L1 ? L1 + ' | ' : ''} ${
        L2 ? L2 + ' | ' : ''
      } ${L3 ? L3 + ' | ' : ''} ${currentPage.page_type}`,
      findingMethodId: FINDING_METHOD_ID.TOP_MENU,
    })
  },
  [FINDING_METHOD_AGGREGATE.CAMPAIGN]: (elementInfo?: { elementName?: string; elementId?: string }) => {
    const { elementName, elementId } = elementInfo ?? {}
    const { currentPage } = getPageContextDetails()
    setCurrentFindingMethod({
      findingMethodAggregate: FINDING_METHOD_AGGREGATE.CAMPAIGN,
      findingMethodGranular: `Campaign | ${elementName} | ${currentPage.page_type}`,
      findingMethodId: elementId ?? '',
    })
  },
  [FINDING_METHOD_AGGREGATE.WISHLIST]: () => {
    setCurrentFindingMethod({
      findingMethodGranular: FINDING_METHOD_AGGREGATE.WISHLIST,
      findingMethodAggregate: FINDING_METHOD_GRANULAR.WISHLIST,
      findingMethodId: FINDING_METHOD_ID.WISHLIST,
    })
  },
  [FINDING_METHOD_AGGREGATE.MY_ORDERS]: () => {
    setCurrentFindingMethod({
      findingMethodAggregate: FINDING_METHOD_AGGREGATE.MY_ORDERS,
      findingMethodGranular: FINDING_METHOD_GRANULAR.MY_ORDERS,
      findingMethodId: FINDING_METHOD_ID.ORDERS,
    })
  },
  [FINDING_METHOD_AGGREGATE.LANDING_PAGE]: () => {
    const { currentPage } = getPageContextDetails()
    const currentFindingMethod = getCurrentFindingMethod()
    if (!currentFindingMethod) {
      setCurrentFindingMethod({
        findingMethodAggregate: FINDING_METHOD_AGGREGATE.LANDING_PAGE,
        findingMethodGranular: `Landing Page | ${currentPage.page_type}`,
        findingMethodId: `${currentPage.page_name}`,
      })
    }
  },
}

export const getFindingMethodAggregate = () =>
  getIsHomePage() ? FINDING_METHOD_AGGREGATE.CAMPAIGN : FINDING_METHOD_AGGREGATE.RECOMMENDATION
